import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FilterListBoard } from 'app/components/lists/filter/FilterListBoard'
import { FilterListTable } from 'app/components/lists/filter/FilterListTable'
import { ListSettingsButton } from 'app/components/lists/ListSettingsButton'
import { ListViewToggle } from 'app/components/lists/ListViewToggle'
import { useOpenListSettings } from 'app/components/lists/useOpenListSettings'
import { useRememberedListView } from 'app/components/lists/useRememberedListView'
import { useDocumentTitle } from 'app/hooks/useDocumentTitle'
import type { List } from 'app/generated/graphql'
import type { ListView } from 'app/types/navigation'
import { useHandleSearchParams } from 'app/utils'
import { FILTER_LIST_VIEWS } from 'lib/lists'

export const FilterListView = observer(function FilterListView({
  list,
  onStagesChanged,
}: {
  list: List
  onStagesChanged: () => void
}): ReactElement {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [savedView, saveView, settingLoaded] = useRememberedListView(list.id, FILTER_LIST_VIEWS)
  const openSettings = useOpenListSettings({ source: `practice`, list }, onStagesChanged)
  useDocumentTitle(list.name)

  // Without a `?view=` to render immediately, wait for the saved view to load so the
  // page never flashes the board before swapping to a remembered table.
  if (!searchParams.has(`view`) && !settingLoaded) return <></>

  const urlView = searchParams.get(`view`)
  const view: ListView = FILTER_LIST_VIEWS.includes(urlView as ListView)
    ? (urlView as ListView)
    : FILTER_LIST_VIEWS.includes(savedView as ListView)
      ? (savedView as ListView)
      : `board`

  const viewToggle = (
    <ListViewToggle
      views={FILTER_LIST_VIEWS}
      current={view}
      onSelect={(next) => {
        saveView(next)
        if (next !== view) handleSearchParams({ view: next })
      }}
    />
  )
  const settings = <ListSettingsButton onClick={openSettings} />

  // Keyed per list: the table's own key is its tab, so without this a row selection made in
  // one list would still be live in the next.
  if (view === `table`)
    return <FilterListTable key={list.id} list={list} viewToggle={viewToggle} titleActions={settings} />
  return (
    <FilterListBoard list={list} viewToggle={viewToggle} titleActions={settings} onStagesChanged={onStagesChanged} />
  )
})
